import { useMediaQuery, useTheme } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import LargeSwitch from 'src/components/LargeSwitch';
import {
  InfoTooltip,
  InfoTooltipButton,
  InfoTooltipText,
} from 'src/pages/payment-msi/PaymentMsiV2/components/InfoTooltip';
import { InfoIcon } from 'src/pages/payment-msi/PaymentMsiV2/icons/InfoIcon';
import { AppTheme } from 'src/themes/muiTheme';

export const PaymentSectionDivider = styled(Divider)(({ theme }) => {
  return {
    borderColor: theme.palette.text.primary,
  };
});

const PaymentSection = styled(
  ({ component = 'article', ...props }: BoxProps) => {
    return <Box component={component} {...props} />;
  },
)(({ theme }) => {
  const mobileMaxSize = theme.breakpoints.values.sm + 1;
  const mobile = theme.breakpoints.down(mobileMaxSize);

  return {
    marginTop: '40px',
    marginBottom: '40px',
    [mobile]: {
      marginTop: '30px',
      marginBottom: '30px',
    },
  };
});

interface MobilePaymentSectionHeaderProps {
  hasSwitch: boolean;
  switchOpen?: boolean;
  onSwitchChange?: () => void;
  sectionTitle: ReactNode;
  sectionDescription?: ReactNode;
  headerProps?: ComponentPropsWithoutRef<typeof Box>;
  sectionTitleProps?: ComponentPropsWithoutRef<typeof Typography>;
}
export const MobilePaymentSectionHeader = ({
  hasSwitch,
  onSwitchChange,
  sectionTitle,
  switchOpen,
  sectionDescription,
  headerProps,
  sectionTitleProps,
}: MobilePaymentSectionHeaderProps) => {
  return (
    <Box component={'header'} {...headerProps}>
      <Typography
        marginBottom={'48px'}
        component={'h3'}
        variant={'h5'}
        {...sectionTitleProps}
      >
        {sectionTitle}
      </Typography>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        {sectionDescription ? (
          <Typography>{sectionDescription}</Typography>
        ) : null}
        {hasSwitch ? (
          <LargeSwitch onChange={onSwitchChange} checked={switchOpen} />
        ) : null}
      </Box>
    </Box>
  );
};

interface DesktopPaymentSectionHeaderProps {
  sectionTitle: ReactNode;
  hasSwitch: boolean;
  switchOpen?: boolean;
  onSwitchChange?: () => void;
  hasTooltip?: boolean;
  tooltipText?: ReactNode;
  headerProps?: ComponentPropsWithoutRef<typeof Box>;
  sectionTitleProps?: ComponentPropsWithoutRef<typeof Typography>;
}
const desktopPaymentSectionHeaderStyles = {
  title: (theme: AppTheme) => {
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      [tablet]: {
        fontSize: '24px',
        lineHeight: '32px',
      },
    };
  },
};
const DesktopPaymentSectionHeader = ({
  sectionTitle,
  hasSwitch,
  hasTooltip,
  onSwitchChange,
  switchOpen,
  tooltipText,
  headerProps,
  sectionTitleProps,
}: DesktopPaymentSectionHeaderProps) => {
  const { sx: sectionTitleSx, ...restSectionTitleProps } =
    sectionTitleProps ?? {};

  const theme = useTheme<AppTheme>();

  const sectionTitleSxProps = {
    ...desktopPaymentSectionHeaderStyles.title(theme),
    ...(typeof sectionTitleSx === 'function'
      ? sectionTitleSx(theme)
      : sectionTitleSx),
  };

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      component={'header'}
      {...headerProps}
    >
      <Box display={'flex'} alignItems={'center'} columnGap={'16px'}>
        <Typography
          component={'h3'}
          variant={'h5'}
          {...restSectionTitleProps}
          sx={sectionTitleSxProps}
        >
          {sectionTitle}
        </Typography>
        {hasTooltip ? (
          <InfoTooltip title={<InfoTooltipText>{tooltipText}</InfoTooltipText>}>
            <InfoTooltipButton>
              <InfoIcon />
            </InfoTooltipButton>
          </InfoTooltip>
        ) : null}
      </Box>
      {hasSwitch ? (
        <LargeSwitch onChange={onSwitchChange} checked={switchOpen} />
      ) : null}
    </Box>
  );
};

interface PaymentSectionHeaderProps {
  sectionTitle: ReactNode;
  hasSwitch: boolean;
  switchOpen?: boolean;
  onSwitchChange?: () => void;
  desktopTooltipText?: ReactNode;
  mobileDescription?: ReactNode;
  headerProps?: ComponentPropsWithoutRef<typeof Box>;
  sectionTitleProps?: ComponentPropsWithoutRef<typeof Typography>;
}
const PaymentSectionHeader = ({
  sectionTitle,
  hasSwitch,
  switchOpen,
  onSwitchChange,
  desktopTooltipText,
  mobileDescription,
  headerProps,
  sectionTitleProps,
}: PaymentSectionHeaderProps) => {
  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  return isMobile ? (
    <MobilePaymentSectionHeader
      hasSwitch={hasSwitch}
      onSwitchChange={onSwitchChange}
      sectionTitle={sectionTitle}
      switchOpen={switchOpen}
      sectionDescription={mobileDescription}
      headerProps={headerProps}
      sectionTitleProps={sectionTitleProps}
    />
  ) : (
    <DesktopPaymentSectionHeader
      hasSwitch={hasSwitch}
      switchOpen={switchOpen}
      onSwitchChange={onSwitchChange}
      sectionTitle={sectionTitle}
      hasTooltip={Boolean(desktopTooltipText)}
      tooltipText={desktopTooltipText}
      headerProps={headerProps}
      sectionTitleProps={sectionTitleProps}
    />
  );
};

export { PaymentSection, PaymentSectionHeader };
